import React from 'react';
import { Route } from 'react-router-dom';
import routes from 'library/Routes';
// Critical path — always in the initial bundle
import HomeView from 'routes/Home/components/HomeView';
import SearchView from 'routes/Search/SearchView';
import StudioView from 'routes/Studio/StudioView';
import BookingView from 'routes/Studio/BookingView';
import GroupBookingView from 'routes/Studio/GroupBookingView';
import BookingCourseView from 'routes/Studio/BookingCourseView';
import BookingClassInfoView from 'routes/Studio/BookingClassInfoView';
import BookingRatePlanView from 'routes/Studio/BookingRatePlanView/BookingRatePlanView';
import UserHelper from 'infrastructure/UserHelper';
import { hideError, hideNeedLoging } from '../../actions/actions';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { ClientAuthorization } from 'infrastructure/ClientAuthorizedComponent';
import { WaitForUserMiddleware } from 'infrastructure/WaitForUserMiddleware';
import { PrivacyPolicy } from '../../routes/PrivacyPolicy';
import { TermsAndConditions } from '../../routes/TermsAndConditions';
import { compose } from 'recompose';
import { renderBizLandingRoutes } from '../../containers/BizLandingRoutes';
import { CookiesPolicy } from 'components/CookiesPolicy';
import { WLWrapper } from 'infrastructure/WLWrapper';
import { GELIOS } from 'constants/whileLabel';
import { analyticsService, PageMapper } from '../../services/analytics/index-student';

// Auth — separate chunk, loaded only when the user navigates to a sign-in/up page
const LoginView = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignIn/LoginView'));
const SignInClientView = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignIn/SignInClientView'));
const SignInClientRedesign = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignInClientRedesign'));
const RedirectLogin = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignIn/RedirectLogin'));
const JoinUsView = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/joinUs/JoinUsView'));
const FBView = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignIn/FBView'));
const SignUpView = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignUp/SignUpView'));
const SignUpRedesign = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignUpRedesign'));
const ConfirmUserView = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignUp/ConfirmUserView'));
const ForgetPasswordView = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignUp/ForgetPasswordView'));
const ResetPasswordView = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/SignUp/ResetPasswordView'));
const ForgetPassRedesign = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/ForgetPassRedesign').then(m => ({ default: m.ForgetPassRedesign })));
const ResetPassRedesign = React.lazy(() => import(/* webpackChunkName: "auth" */ 'routes/ResetPassRedesign').then(m => ({ default: m.ResetPassRedesign })));

// Account — loaded only when a logged-in user edits their profile
const EditClientAccountView = React.lazy(() => import(/* webpackChunkName: "account" */ 'routes/EditAccount/EditClientAccountView'));
const ChangeClientEmailView = React.lazy(() => import(/* webpackChunkName: "account" */ 'routes/EditAccount/ChangeClientEmailView'));
const ChangeEmailRedesign = React.lazy(() => import(/* webpackChunkName: "account" */ 'routes/ChangeEmailRedesign').then(m => ({ default: m.ChangeEmailRedesign })));
const ChangeClientPasswordView = React.lazy(() => import(/* webpackChunkName: "account" */ 'routes/EditAccount/ChangeClientPasswordView'));

// Misc — secondary pages, split to reduce initial bundle
const ConnectMeWithOwner = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/MyClasses/ConnectMeWithOwner'));
const MyStudioClassesView = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/MyClasses/MyStudioClassesView'));
const ClientCabinetPage = React.lazy(() => import(/* webpackChunkName: "misc" */ 'feature-client-cabinet').then(m => ({ default: m.ClientCabinetPage })));
const OwnerPaymentInfo = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/MyClasses/OwnerPaymentInfo'));
const TermsAndConditionsView = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/MyClasses/TermsAndConditionsView'));
const UnsubscribeFromNewsLetters = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/PendingClients/UnsubscribeFromNewsLetters'));
const MyQrCodeView = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/MyClasses/MyQrCodeView'));
const VerifyGuestAttendance = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/StudioRegistrations/VerifyGuestAttendance'));
const ClientAttendance = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/StudioRegistrations/ClientAttendance'));
const CancelClientAttendance = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/StudioRegistrations/CancelClientAttendance'));
const InClientShoesView = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/SignIn/InClientShoesView'));
const PendingReviewView = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/PendingReview/PendingReviewView'));
const AskQuestionForm = React.lazy(() => import(/* webpackChunkName: "misc" */ 'routes/Messages/AskQuestionForm'));

// Widgets — isolated chunk for embedded third-party usage
const ScheduleWidget = React.lazy(() => import(/* webpackChunkName: "widgets" */ '../../routes/Widgets/ScheduleWidget').then(m => ({ default: m.ScheduleWidget })));
const CourseWidget = React.lazy(() => import(/* webpackChunkName: "widgets" */ '../../routes/Widgets/CourseWidget').then(m => ({ default: m.CourseWidget })));
const ClassWidget = React.lazy(() => import(/* webpackChunkName: "widgets" */ '../../routes/Widgets/ClassWidget').then(m => ({ default: m.ClassWidget })));

const mapDispatchToProps = (dispatch) => {
  return {
    hideError: function () {
      dispatch(hideError());
    },
    hideNeedLogin: function () {
      dispatch(hideNeedLoging());
    },
  };
};

function mapStateToProps(state, ownProps) {
  return {
    translate: getTranslate(state.locale),
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(
  class CoreLayout extends React.Component {

    componentDidMount() {
      this.trackPageView();
    }

    componentDidUpdate(prevProps) {
      if (prevProps.location?.pathname !== this.props.location?.pathname) {
        this.trackPageView();
      }
    }

    trackPageView = () => {
      const { location } = this.props;

      const pageInfo = PageMapper.getPageInfo(location.pathname);

      const pageData = {
        path: location.pathname,
        search: location.search,
        title: document.title,
        page_name: pageInfo.name,
        page_category: pageInfo.category,
        app_type: 'student',
        timestamp: new Date().toISOString()
      };

      analyticsService.page(pageData);

      
    };

    render() {
      return (
        <div className="layout-root">
          <div className="layout-content">
            <React.Suspense fallback={null}>
            {renderBizLandingRoutes()}
            <Route
              exact
              path={routes.root()}
              component={ClientAuthorization(HomeView, [], false, true, true)}
            />
            <Route
              exact
              path={routes.privacyPolicy()}
              component={PrivacyPolicy}
            />
            <Route
              exact
              path={routes.termsAndConditions()}
              component={ClientAuthorization(TermsAndConditions, [])}
            />
            <Route
              exact
              path={routes.search()}
              component={ClientAuthorization(SearchView, [])}
            />
            <Route
              exact
              path={routes.seoSearch()}
              component={ClientAuthorization(SearchView, [])}
            />
            <Route
              exact
              path={routes.seoSearchCategory()}
              component={ClientAuthorization(SearchView, [])}
            />
            <Route
              exact
              path={routes.seoSearchPromo()}
              component={ClientAuthorization(SearchView, [])}
            />
            <Route
              exact
              path={routes.login()}
              component={WaitForUserMiddleware(
                ClientAuthorization(LoginView, [])
              )}
            />
            <Route
              exact
              path={routes.clientLogin()}
              component={WaitForUserMiddleware(
                WLWrapper(ClientAuthorization(SignInClientView, []), {
                  [GELIOS]: ClientAuthorization(
                    SignInClientRedesign,
                    [],
                    false,
                    true,
                    true
                  ),
                })
              )}
            />
            <Route
              exact
              path={routes.redirectLogin()}
              component={WaitForUserMiddleware(
                ClientAuthorization(RedirectLogin, [])
              )}
            />
            <Route
              exact
              path={routes.joinUs()}
              component={ClientAuthorization(JoinUsView, [])}
            />
            <Route
              exact
              path={routes.connect()}
              component={ClientAuthorization(ConnectMeWithOwner, [])}
            />
            <Route
              exact
              path={routes.fb()}
              component={ClientAuthorization(FBView, [])}
            />
            <Route
              exact
              path={routes.signUp()}
              component={WLWrapper(ClientAuthorization(SignUpView, []), {
                [GELIOS]: ClientAuthorization(
                  SignUpRedesign,
                  [],
                  false,
                  true,
                  true
                ),
              })}
            />
            <Route
              exact
              path={routes.confirm()}
              component={ClientAuthorization(ConfirmUserView, [])}
            />
            <Route
              exact
              path={routes.forgetPassword()}
              component={WLWrapper(
                ClientAuthorization(ForgetPasswordView, []),
                {
                  [GELIOS]: ClientAuthorization(
                    ForgetPassRedesign,
                    [],
                    false,
                    true,
                    true
                  ),
                }
              )}
            />
            <Route
              exact
              path={routes.resetPassword()}
              component={WLWrapper(ClientAuthorization(ResetPasswordView, []), {
                [GELIOS]: ClientAuthorization(
                  ResetPassRedesign,
                  [],
                  false,
                  true,
                  true
                ),
              })}
            />
            <Route
              exact
              path={routes.studioId()}
              component={ClientAuthorization(StudioView, [])}
            />
            <Route
              exact
              path={routes.studio()}
              component={ClientAuthorization(StudioView, [])}
            />
            <Route
              exact
              path={routes.booking()}
              component={ClientAuthorization(BookingView, [])}
            />
            <Route
              exact
              path={routes.groupBooking()}
              component={ClientAuthorization(GroupBookingView, [], false, true)}
            />
            <Route
              exact
              path={routes.courseBooking()}
              component={ClientAuthorization(BookingCourseView, [])}
            />
            <Route
              exact
              path={routes.classId()}
              component={WaitForUserMiddleware(
                ClientAuthorization(BookingClassInfoView, [], false, true)
              )}
            />
            <Route
              exact
              path={routes.class()}
              component={WaitForUserMiddleware(
                ClientAuthorization(BookingClassInfoView, [], false, true)
              )}
            />
            <Route
              exact
              path={routes.bookingRatePlan()}
              component={WaitForUserMiddleware(
                ClientAuthorization(BookingRatePlanView, [], false, true)
              )}
            />
            <Route
              exact
              path={routes.verifyGuest()}
              component={VerifyGuestAttendance}
            />
            <Route
              exact
              path={routes.clientAttendance()}
              component={ClientAuthorization(ClientAttendance, [], false, true)}
            />
            <Route
              exact
              path={routes.cancelClientAttendance()}
              component={ClientAuthorization(
                CancelClientAttendance,
                [],
                false,
                true
              )}
            />
            <Route
              exact
              path={routes.pendingReview()}
              component={ClientAuthorization(PendingReviewView, [])}
            />
            <Route
              exact
              path={routes.myAccount()}
              component={WaitForUserMiddleware(
                ClientAuthorization(EditClientAccountView, [UserHelper.STUDENT])
              )}
            />
            <Route
              exact
              path={routes.changePassword()}
              component={WaitForUserMiddleware(
                ClientAuthorization(ChangeClientPasswordView, [
                  UserHelper.STUDENT,
                ])
              )}
            />
            <Route
              exact
              path={routes.studioClientClasses()}
              component={ClientAuthorization(
                MyStudioClassesView,
                [],
                false,
                true
              )}
            />
            <Route
              exact
              path={routes.studioClientClassesGelios()}
              component={ClientAuthorization(
                ClientCabinetPage,
                [UserHelper.STUDENT],
                false,
                true,
                true,
                routes.clientLogin()
              )}
            />

            <Route
              exact
              path={routes.ownerPaymentInfo()}
              component={ClientAuthorization(OwnerPaymentInfo, [], false, true)}
            />
            <Route
              exact
              path={routes.ownerTermsAndConditionsHtml()}
              component={TermsAndConditionsView}
            />
            <Route
              exact
              path={routes.unsubscribe()}
              component={ClientAuthorization(UnsubscribeFromNewsLetters, [])}
            />
            <Route
              exact
              path={routes.me()}
              component={WaitForUserMiddleware(
                ClientAuthorization(MyQrCodeView, [UserHelper.STUDENT])
              )}
            />
            <Route
              exact
              path={routes.changeEmail()}
              component={WLWrapper(
                WaitForUserMiddleware(
                  ClientAuthorization(ChangeClientEmailView, [
                    UserHelper.STUDENT,
                  ])
                ),
                {
                  [GELIOS]: WaitForUserMiddleware(
                    ClientAuthorization(
                      ChangeEmailRedesign,
                      [UserHelper.STUDENT],
                      false,
                      true,
                      true
                    )
                  ),
                }
              )}
            />

            <Route
              exact
              path={routes.inShoes()}
              component={WaitForUserMiddleware(
                ClientAuthorization(InClientShoesView, [UserHelper.STUDENT])
              )}
            />
            <Route
              exact
              path={routes.scheduleWidget()}
              component={ScheduleWidget}
            />
            <Route
              exact
              path={routes.courseWidget()}
              component={CourseWidget}
            />
            <Route exact path={routes.classWidget()} component={ClassWidget} />
            <Route
              exact
              path={routes.askQuestionPublic()}
              component={ClientAuthorization(AskQuestionForm, [])}
            />
            </React.Suspense>
          </div>
          <CookiesPolicy />
        </div>
      );
    }
  }
);
